-- | Step 3 comments widget for a document.
-- |
-- | Renders the comment list and asks the app runtime to post, edit, or delete
-- | comments.
module Widgets.Comments where

import Common

import API.Comments (Comment, CommentID, CommentInput)
import API.Document (DocPolicy)
import API.Types (DocumentID)
import Concur.React.DOM as D
import Concur.React.Props as P
import Data.Array (catMaybes, concatMap)
import Data.String as Str
import Internal.Luxon as DT
import Internal.Util (justIf)
import Widgets.Checkbox (labelCheckbox)
import Widgets.Confirmation (confirmModal)
import Widgets.Dropdown (dropdownButton)

type CommentsBase r =
    { id :: String -- ^ ID of this widget
    , formId :: DocumentID -- ^ ID of the form we're commenting on.
    , comments :: Array Comment
    , can :: DocPolicy
    | r
    }

type Comments = CommentsBase ( post :: CommentState )
type PostFormState = { text :: String, public :: Boolean }
data CommentState = Posting PostFormState | Editing CommentID PostFormState

data CommentFormResult
    = PostComment
    | DeleteComment CommentID
    | EditComment CommentID
    | StopEditing
    | StateChange Comments

data CommentRequest
    = CreateComment CommentInput
    | UpdateComment CommentID CommentInput
    | RemoveComment CommentID

data CommentsResult
    = CommentsChanged Comments
    | CommentsRequest CommentRequest

initComments :: CommentsBase () -> Comments
initComments { id, formId, comments, can } =
    { id, formId, comments, can
    , post: Posting initPostForm
    }

initPostForm :: PostFormState
initPostForm = { text: "", public: false }


runComments :: Comments -> Widget HTML CommentsResult
runComments state = handleResult state =<< renderComments state

handleResult :: Comments -> CommentFormResult -> Widget HTML CommentsResult
handleResult _ (StateChange state') = pure $ CommentsChanged state'
handleResult state StopEditing = pure $ CommentsChanged state { post = Posting initPostForm }
handleResult state (EditComment id) = do
    case find (\c -> c.id == id) state.comments of
        Just comment -> pure $ CommentsChanged state { post = Editing id { text: comment.comment, public: comment.public } }
        Nothing -> pure $ CommentsChanged state -- Should be unreachable
handleResult state (DeleteComment comId) = do
    conf <- confirmModal (renderComments state)
        (lookupTranslation "confirm-delete-comment-title")
        (lookupTranslation "confirm-delete-comment-primary-button")
        (lookupTranslation "confirm-delete-comment")
    if conf
        then pure $ CommentsRequest $ RemoveComment comId
        else pure $ CommentsChanged state
handleResult { post: Posting { text, public: public' }, can } PostComment = do
    let public = if can.private_comment then public' else true
    pure $ CommentsRequest $ CreateComment { comment: text, public }
handleResult { post: Editing comId { text, public } } PostComment = do
    pure $ CommentsRequest $ UpdateComment comId { comment: text, public }

renderComments :: Comments -> Widget HTML CommentFormResult
renderComments state@{ id, comments, post } = D.details
    [ P.key id, P._id id, P.open $ length comments > 0 ]
    [ D.summary'
        [ D.h1
            [ P.className "comment-section-heading" ]
            [ D.text $ lookupTranslation "comment-section" ]
        ]
    , D.div [ P.className "comment-section" ] $ catMaybes
        [ Just $ D.div [ P.className "comment-section-list" ] $ map (renderComment state) comments
        , justIf (isPosting && state.can.comment) $ commentForm state
        ]
    ]
  where
    isPosting = case post of
        Posting _ -> true
        _ -> false

renderComment :: Comments -> Comment -> Widget HTML CommentFormResult
renderComment state@{ post: Editing eid _ } { id } | id == eid = D.div
    [ P.className "comment" ]
    [ commentForm state ]
renderComment _ com@{ id, author, comment, can } = D.div
    [ P.className "comment" ]
    [ D.div [ P.className "comment-head" ] $ catMaybes
        [ Just $ D.h1 [ P.className "comment-poster" ]
            [ D.text (lookupTranslation "comment-by" <> author.name) ]
        , Just $ D.div [ P.className "comment-times" ] $ catMaybes
            [ Just $
                D.text $ DT.toRelative com.created_at
            , justIf (com.updated_at /= com.created_at) $
                D.text $ " (" <> lookupTranslation "comment-updated-at" <> " "
                            <> DT.toRelative com.updated_at <> ")"
            ]
        , Just $ D.div [ P.className "comment-is-public" ]
            [ D.text $ lookupTranslation $ if com.public
                then "comment-public-indicator"
                else "comment-private-indicator"
            ]
        , maybeDropBtn $ catMaybes
            [ justIf can.update
                { value: EditComment id
                , label: lookupTranslation "edit-comment" }
            , justIf can.delete
                { value: DeleteComment id
                , label: lookupTranslation "delete-comment" }
            ]
        ]
    , D.div [ P.className "comment-body" ]
        $ concatMap (\l -> [D.text l, D.br']) commentLines
    ]
  where
    commentLines = Str.split (Str.Pattern "\n") comment

    maybeDropBtn [] = Nothing
    maybeDropBtn menu = Just $ dropdownButton renderDBtn menu

    renderDBtn = D.button
        [ P.className "dropdown-button", P.onClick]
        [ D.text "⠇" ]

commentForm :: Comments -> Widget HTML CommentFormResult
commentForm state@{ id, post: post' } = D.div
    [ P.className "comment-form" ] $ catMaybes
    [ Just $ D.textarea
        [ P.key $ id <> "_textarea"
        , P.className "comment-form-textarea"
        , P.placeholder $ lookupTranslation "comment-form-placeholder"
        , P.value post.text
        , StateChange
            <$> (\t -> state { post = wrapPState $ post { text = t } })
            <$> P.unsafeTargetValue <$> P.onInput
        ] []
    , justIf (state.can.private_comment) $
        StateChange <$> setPublic <$>
        labelCheckbox "comment-public-check" (lookupTranslation "comment-public-check") post.public
    , Just $ D.div [ P.className "comment-buttons" ] $ catMaybes
        [ Just $ D.button
            [ P.className "btn comment-form-post-button"
            , PostComment <$ P.onClick
            ] [ D.text $ lookupTranslation buttonLbl ]
        , justIf isEditing $ D.button
            [ P.className "btn comment-form-cancel-button"
            , StopEditing <$ P.onClick
            ] [ D.text $ lookupTranslation "cancel-edit-comment" ]
        ]
    ]
  where
    wrapPState p = case post' of
        Editing cid _ -> Editing cid p
        Posting _ -> Posting p
    post = case post' of
        Editing _ p -> p
        Posting p -> p
    isEditing = case post' of
        Editing _ _ -> true
        Posting _ -> false
    setPublic pub = case post' of
        Editing i p -> state { post = Editing i $ p { public = pub } }
        Posting p -> state { post = Posting $ p { public = pub } }
    buttonLbl = case post' of
        Editing _ _ -> "save-comment"
        Posting _ -> "post-comment"
